import { PWBWorker } from 'promise-worker-bi';

// optional chaining is not supported here
/* eslint-disable @typescript-eslint/prefer-optional-chain */

const promiseWorker = new PWBWorker();

const CHANNELS = ['email', 'twilio', 'push', 'webhook', 'slack', 'urban_airship', 'in_app'];

function sumArray(arr) {
  if (arr.length === 0) {
    return 0;
  }

  return arr.reduce((a, b) => a + b, 0);
}

function calculateJourneyInWorkflowData(started, exitedEarly, neverActivated, finished) {
  return started.map((started, index) => started - (exitedEarly[index] + neverActivated[index] + finished[index]));
}

function calculateRatios(numerators, denominators) {
  if (numerators == null || denominators == null) {
    return [];
  }

  // Assume that numerators line up with denominators from beginning. If
  // different lengths, slice from the start.
  if (numerators.length < denominators.length) {
    denominators = denominators.slice(0, numerators.length - 1);
  }

  return denominators.map((value, i) => {
    if (value == null || value === 0) {
      return null;
    }

    return numerators[i] / value;
  });
}

function calculateRatesAndTotals(
  data,
  measures,
  measureType,
  selectedChannels,
  findDenominator,
  perChannelMetrics,
  metricsPerActionType,
) {
  let rateAndTotalData = {};
  measures.forEach((measure) => {
    let numeratorTotal = sumArray(data[measure]);
    rateAndTotalData[`total_${measure}`] = numeratorTotal;

    if (measureType === 'delivery_metrics') {
      let denominatorTotal = selectedChannels.reduce((acc, channel) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!metricsPerActionType[`${channel}_action`].includes(measure)) {
          return acc;
        }
        let denominator = findDenominator(measure, 'delivery_metrics', channel);
        if (denominator == null) {
          return acc;
        }

        let total = perChannelMetrics[channel][`total_${denominator}`];

        if (total == null) {
          return acc;
        }

        if (acc == null) {
          return total;
        }

        return acc + total;
      }, null);

      rateAndTotalData[`${measure}_rate`] = denominatorTotal > 0 ? numeratorTotal / denominatorTotal : null;

      let denominatorSeries = selectedChannels.reduce((acc, channel) => {
        let denominator = findDenominator(measure, 'delivery_metrics', channel);
        if (denominator == null) {
          return acc;
        }

        return perChannelMetrics[channel][denominator].map((v, i) => {
          let currentValue = acc[i];

          if (currentValue == null) {
            return v;
          }

          if (v == null) {
            return currentValue;
          }

          return v + currentValue;
        });
      }, new Array(data[measure].length).fill(null));

      rateAndTotalData[`${measure}_rates`] = calculateRatios(data[measure], denominatorSeries);
    } else if (measureType === 'journey_metrics') {
      let denominator = findDenominator(measure, measureType);

      if (denominator == null) {
        return;
      }

      let denominatorTotal = sumArray(data[denominator]);
      rateAndTotalData[`${measure}_rate`] = denominatorTotal > 0 ? numeratorTotal / denominatorTotal : 0;
      rateAndTotalData[`${measure}_rates`] = calculateRatios(data[measure], data[denominator]);
    }
  });
  return rateAndTotalData;
}

export default class ProcessAnalysisMetricsWorker {
  constructor() {
    this.findDenominator = this.findDenominator.bind(this);
  }

  findDenominator(measure, measureType, channel) {
    if (measureType === 'delivery_metrics') {
      let measureDefinition = this.deliveryMetricsDenominators[measure];

      if (
        measureDefinition != null &&
        measureDefinition.actionTypeDenominators != null &&
        measureDefinition.actionTypeDenominators[`${channel}_action`] != null
      ) {
        return measureDefinition.actionTypeDenominators[`${channel}_action`];
      }

      return measureDefinition.denominator;
    }

    if (measureType === 'journey_metrics') {
      return this.journeyMetricsDenominators[measure];
    }

    return null;
  }

  process(
    metrics,
    selectedChannels,
    timeRangeSteps,
    excludeZeros,
    excludeNewslettersWithoutSends,
    excludeTransactionalDefault,
    {
      DELIVERY_METRICS_MEASURES,
      JOURNEY_METRICS_MEASURES,
      DELIVERY_METRICS_MEASURE_DENOMINATORS,
      JOURNEY_METRICS_MEASURE_DENOMINATORS,
      METRICS_PER_ACTION_TYPE,
    },
    subscriptionTopics,
  ) {
    this.deliveryMetricsDenominators = DELIVERY_METRICS_MEASURE_DENOMINATORS;
    this.journeyMetricsDenominators = JOURNEY_METRICS_MEASURE_DENOMINATORS;

    let processedMetrics = metrics.map((item) => {
      let summedData = {
        delivery_metrics: {},
        per_channel_metrics: {},
        per_subscription_topic_metrics: {},
        journey_metrics: {},
        name: item.name,
        type: item.type,
        icon: item.icon,
        is_default: item.is_default,
        id: item.id,
        _id: item._id,
        channel: null,
        conversion: item.conversion,
      };

      let { data } = item;

      if (item.type === 'newsletter') {
        data.delivery_metrics = data.newsletter_metrics;
        summedData.channel = Object.keys(data.newsletter_metrics)[0];
      }

      if (item.type === 'transactional_message') {
        data.delivery_metrics = data.transactional_metrics;
        summedData.channel = Object.keys(data.transactional_metrics)[0];
      }

      CHANNELS.forEach((channel) => {
        let perChannelMetrics = {};
        let channelMetrics = data.delivery_metrics[channel];

        if (channelMetrics == null) {
          DELIVERY_METRICS_MEASURES.forEach((measure) => {
            perChannelMetrics[measure] = [...new Array(timeRangeSteps)].fill(0);
          });
        } else {
          DELIVERY_METRICS_MEASURES.forEach((measure) => {
            // Don't calculate "converted" metrics if the item doesn't have
            // conversions enabled.
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (channelMetrics[measure] == null || (measure === 'converted' && !item.conversion)) {
              perChannelMetrics[measure] = [...new Array(timeRangeSteps)].fill(0);
            } else {
              perChannelMetrics[measure] = [...channelMetrics[measure]];
            }

            if (measure === 'clicked_to_opened') {
              // Copy "clicked" data into "clicked_to_opened", which uses the same
              // numerator, but a different denominator for rates
              perChannelMetrics.clicked_to_opened = [...channelMetrics.clicked];
            }

            perChannelMetrics[`total_${measure}`] = sumArray(perChannelMetrics[measure]);

            let denominator = this.findDenominator(measure, 'delivery_metrics', channel);
            if (denominator != null) {
              perChannelMetrics[`${measure}_rates`] = calculateRatios(
                channelMetrics[measure],
                channelMetrics[denominator],
              );
            }
          });
        }

        summedData.per_channel_metrics[channel] = perChannelMetrics;
      });

      DELIVERY_METRICS_MEASURES.forEach((measure) => {
        summedData.delivery_metrics[measure] = [...new Array(timeRangeSteps)].fill(0);
      });

      selectedChannels.forEach((channel) => {
        let channelMetrics = data.delivery_metrics[channel];

        if (channelMetrics == null) {
          return;
        }

        DELIVERY_METRICS_MEASURES.forEach((measure) => {
          let measureData = channelMetrics[measure];

          if (measure === 'clicked_to_opened') {
            measureData = channelMetrics.clicked;
          }

          // Don't calculate "converted" metrics if the item doesn't have
          // conversions enabled.
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (measure === 'converted' && !item.conversion) {
            return;
          }

          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (!METRICS_PER_ACTION_TYPE[`${channel}_action`].includes(measure)) {
            return;
          }

          if (measureData != null) {
            summedData.delivery_metrics[measure] = measureData.map((value, index) => {
              let currentValue = summedData.delivery_metrics[measure][index];
              return value + currentValue;
            });
          }
        });

        Object.entries(data.subscription_topic_metrics ?? {}).forEach(([topicId, topicMetrics]) => {
          if (topicMetrics != null && subscriptionTopics != null) {
            let name = subscriptionTopics.find((topic) => topic.id === topicId)?.name;
            if (name == null) {
              name = `deleted topic_${topicId}`;
            }

            summedData.per_subscription_topic_metrics[name] = (topicMetrics.topic_unsubscribed ?? []).reduce(
              (acc, val) => acc + val,
              0,
            );
          }
        });
      });

      summedData.delivery_metrics = {
        ...summedData.delivery_metrics,
        ...calculateRatesAndTotals(
          summedData.delivery_metrics,
          DELIVERY_METRICS_MEASURES,
          'delivery_metrics',
          selectedChannels,
          this.findDenominator,
          summedData.per_channel_metrics,
          METRICS_PER_ACTION_TYPE,
        ),
      };

      if (!['newsletter', 'transactional_message'].includes(item.type)) {
        JOURNEY_METRICS_MEASURES.forEach((measure) => {
          let apiData = data.journey_metrics[measure];
          if (apiData == null) {
            summedData.journey_metrics[measure] = [...new Array(timeRangeSteps)].fill(0);
          } else {
            summedData.journey_metrics[measure] = apiData;
          }
        });

        summedData.journey_metrics.in_workflow = calculateJourneyInWorkflowData(
          summedData.journey_metrics.started,
          summedData.journey_metrics.exited_early,
          summedData.journey_metrics.never_activated,
          summedData.journey_metrics.finished,
        );

        summedData.journey_metrics = {
          ...summedData.journey_metrics,
          ...calculateRatesAndTotals(
            summedData.journey_metrics,
            [...JOURNEY_METRICS_MEASURES, 'in_workflow'],
            'journey_metrics',
            selectedChannels,
            this.findDenominator,
            summedData.per_channel_metrics,
            METRICS_PER_ACTION_TYPE,
          ),
        };
      }

      return summedData;
    });

    // If this flag is enabled, filter out newsletters that have no sends (leave
    // campaigns/apitbs without sends)
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (excludeNewslettersWithoutSends) {
      processedMetrics = processedMetrics.filter((item) => {
        if (item.type !== 'newsletter') {
          return true;
        }

        return item.delivery_metrics.total_sent > 0;
      });
    }

    // If the "exclude zeros" flag is enabled, filter out all items that have no
    // "sent" deliveries
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (excludeZeros) {
      processedMetrics = processedMetrics.filter((item) => item.delivery_metrics.total_sent !== 0);
    }

    // Filter out the "All categorized messages" transactional item if there are
    // no sends there
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (excludeTransactionalDefault) {
      processedMetrics = processedMetrics.filter(
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        (item) => item.type !== 'transactional_message' || !item.is_default || item.delivery_metrics.total_sent !== 0,
      );
    }

    return processedMetrics;
  }
}

promiseWorker.register(async (processArgs) => {
  let processWorker = new ProcessAnalysisMetricsWorker();

  return processWorker.process(...processArgs);
});
